import { formatCurrencyInputToNumber, isEmpty } from "./query-string";
import {
  FieldValues,
  Path,
  UseFormClearErrors,
  UseFormSetError,
} from "react-hook-form";

interface ValidateMinAmountParams<TFieldValues extends FieldValues> {
  watchedAmount: unknown;
  clearErrors: UseFormClearErrors<TFieldValues>;
  setError: UseFormSetError<TFieldValues>;
  fieldName?: Path<TFieldValues>;
  min?: number;
  message?: string;
}

export const validateMinCurrencyAmount = <TFieldValues extends FieldValues>({
  watchedAmount,
  clearErrors,
  setError,
  fieldName,
  min = 1,
  message = "El valor debe ser mayor o igual a $1.",
}: ValidateMinAmountParams<TFieldValues>) => {
  const field = fieldName ?? ("request_amount" as Path<TFieldValues>);

  clearErrors(field);

  if (isEmpty(watchedAmount)) {
    setError(field, {
      type: "manual",
      message,
    });
  } else {
    const monto = formatCurrencyInputToNumber(watchedAmount as string);
    if (monto < min) {
      setError(field, {
        type: "manual",
        message,
      });
    } else {
      clearErrors(field);
    }
  }
};

export interface ValidateTonParams<TFieldValues extends FieldValues> {
  watchedTon: unknown;
  clearErrors: UseFormClearErrors<TFieldValues>;
  setError: UseFormSetError<TFieldValues>;
  fieldName?: Path<TFieldValues>;
  min?: number;
  message?: string;
}

export const validateTon = <TFieldValues extends FieldValues>({
  watchedTon,
  clearErrors,
  setError,
  fieldName,
  min = 1,
  message = "El valor debe ser mayor o igual a 1.",
}: ValidateTonParams<TFieldValues>) => {
  const field = fieldName ?? ("ton" as Path<TFieldValues>);
  clearErrors(field);

  if (isEmpty(watchedTon)) {
    setError(field, {
      type: "manual",
      message,
    });
  } else {
    const ton = Number(String(watchedTon).replace(",", "."));

    if (ton < min) {
      setError(field, {
        type: "manual",
        message,
      });
    } else {
      clearErrors(field);
    }
  }
};

export interface ValidateDeliveryMonthParams<TFieldValues extends FieldValues> {
  watchedDeliveryMonth: unknown;
  clearErrors: UseFormClearErrors<TFieldValues>;
  setError: UseFormSetError<TFieldValues>;
  fieldName?: Path<TFieldValues>;
}

export const validateDeliveryMonth = <TFieldValues extends FieldValues>({
  watchedDeliveryMonth,
  clearErrors,
  setError,
  fieldName,
}: ValidateDeliveryMonthParams<TFieldValues>) => {
  const field = fieldName ?? ("delivery" as Path<TFieldValues>);
  clearErrors(field);

  if (isEmpty(watchedDeliveryMonth)) {
    setError(field, {
      type: "manual",
      message: "Campo requerido.",
    });
  } else {
    clearErrors(field);
  }
};

export interface ValidateAvailableTonParams<TFieldValues extends FieldValues> {
  watchedValues: TFieldValues;
  fieldName?: Path<TFieldValues>;
  setError: UseFormSetError<TFieldValues>;
  clearErrors: UseFormClearErrors<TFieldValues>;
}

export const validateAvailableTon = <TFieldValues extends FieldValues>({
  watchedValues,
  setError,
  fieldName,
  clearErrors,
}: ValidateAvailableTonParams<TFieldValues>) => {
  const field = fieldName ?? ("ton" as Path<TFieldValues>);
  if (!isEmpty(watchedValues?.available_ton)) {
    const tonNum = Number.parseFloat(
      String(watchedValues?.ton ?? "").replace(",", "."),
    );
    const availableNum = Number.parseFloat(
      String(watchedValues?.available_ton ?? "0")
        .toString()
        .replace(",", "."),
    );
    if (
      !Number.isNaN(tonNum) &&
      !Number.isNaN(availableNum) &&
      tonNum > availableNum
    ) {
      setError(field, {
        message: `La cantidad de grano disponible es: ${Number(availableNum).toLocaleString("es-AR")} Tn.`,
      });
    } else {
      clearErrors(field);
    }
  }
};
